import { memo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy } from "lucide-react";
import { toast } from "react-toastify";
import { MemoizedMarkdown } from "./MemoizedMarkdown";

const MessageItem = memo(({ msg, user, formatTime, systemImage }) => {
  const [copied, setCopied] = useState(undefined);

  const stripMarkdown = (text) => {
    return text
      .replace(/#{1,6}\s+/g, "") // headings
      .replace(/\*\*(.+?)\*\*/g, "$1") // bold
      .replace(/\*(.+?)\*/g, "$1") // italic
      .replace(/__(.+?)__/g, "$1") // bold underscore
      .replace(/_(.+?)_/g, "$1") // italic underscore
      .replace(/~~(.+?)~~/g, "$1") // strikethrough
      .replace(/`{3}[\s\S]*?`{3}/g, "") // fenced code blocks
      .replace(/`(.+?)`/g, "$1") // inline code
      .replace(/\[(.+?)\]\(.+?\)/g, "$1") // links
      .replace(/^[-*+]\s+/gm, "") // unordered lists
      .replace(/^\d+\.\s+/gm, "") // ordered lists
      .replace(/^>\s+/gm, "") // blockquotes
      .replace(/^-{3,}$/gm, "") // horizontal rules
      .replace(/\n{3,}/g, "\n\n") // excess newlines
      .trim();
  };

  const handleCopy = async () => {
    try {
      setCopied(msg.id);
      await navigator.clipboard.writeText(stripMarkdown(msg.text));
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(undefined), 3000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <div
      className={`flex ${
        msg.role === "user" ? "justify-end" : "justify-start"
      }`}
    >
      {msg.role === "system" ? (
        <img
          src={systemImage}
          className="w-8 h-8 rounded-full overflow-hidden border uppercase border-white/10 flex items-center justify-center bg-gray-700 text-white text-sm font-bold m-2"
          alt="System"
        />
      ) : (
        <div className="order-2 ml-2">
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.2 }}
            className="w-8 h-8 rounded-full overflow-hidden border border-white/10 flex items-center justify-center bg-gray-700 text-white text-xs font-bold"
          >
            {user.username.slice(0, 2)}
          </motion.div>
        </div>
      )}

      <div
        className={`flex flex-col ${
          msg.role === "system" ? "items-start" : "items-end order-1"
        } gap-1 max-w-[85%]`}
      >
        <div className="text-xs text-black/90 italic flex items-center gap-2">
          <span>{msg.role === "system" ? "Assistant" : "You"}</span>
          {msg.timestamp && (
            <span className="text-black text-[10px]">
              {formatTime(msg.timestamp)}
            </span>
          )}
        </div>

        <div
          className={`p-4 rounded-2xl text-white ${
            msg.type === "image" ? "max-w-2xl" : "max-w-5xl"
          } bg-gradient-to-r from-primary/90 to-secondary/90 shadow-lg ${
            msg.role === "system" ? "rounded-tl-none " : " rounded-tr-none "
          } ${msg.error ? "border-red-500/50" : ""}`}
          style={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          <div className="prose prose-invert max-w-none">
            {msg.type === "image" && msg.image ? (
              <img
                src={msg.image}
                alt="Uploaded"
                className="max-w-full h-auto rounded-lg border border-white/10"
              />
            ) : msg.type === "pdf" && msg.pdfFile ? (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex-shrink-0">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white/95 truncate">
                    {msg.fileName || "PDF Document"}
                  </p>
                  <p className="text-xs text-white/60">PDF File</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(msg.pdfFile, "_blank");
                  }}
                  className="flex-shrink-0 px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  View PDF
                </button>
              </div>
            ) : msg.type === "docx" && msg.docxFile ? (
              <div className="flex items-center gap-3 p-3  bg-white/5 rounded-lg border border-white/10">
                <div className="flex-shrink-0">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white/95 truncate">
                    {msg.fileName || "DOCX Document"}
                  </p>
                  <p className="text-xs text-white/60">DOCX File</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(msg.docxFile, "_blank");
                  }}
                  className="flex-shrink-0 px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  View DOCX
                </button>
              </div>
            ) : msg.type === "csv" && msg.csvFile ? (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex-shrink-0">
                  <svg
                    className="w-8 h-8 text-emerald-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white/95 truncate">
                    {msg.fileName || "CSV Document"}
                  </p>
                  <p className="text-xs text-white/60">CSV File</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(msg.csvFile, "_blank");
                  }}
                  className="flex-shrink-0 px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  View CSV
                </button>
              </div>
            ) : msg.role === "system" ? (
              <div className="text-white/95 prose prose-invert text-wrap max-w-none leading-relaxed chat-message antialiased">
                <MemoizedMarkdown id={msg.id} content={msg.text} />
              </div>
            ) : (
              <p
                dangerouslySetInnerHTML={{
                  __html: msg.text.replace(/\n/g, "<br>"),
                }}
                className="text-sm text-white/95 m-0 leading-relaxed"
              />
            )}
          </div>
        </div>

        {msg.type !== "image" && msg.type !== "docx" && msg.type !== "csv" && (
          <>
            {copied && msg.id === copied ? (
              <div className="text-green-500 flex items-center gap-1">
                <Check
                  // onClick={handleCopy}
                  size={15}
                />
                <small>copied</small>
              </div>
            ) : (
              <Copy
                onClick={handleCopy}
                size={15}
                className="text-gray-500 hover:text-gray-600 cursor-pointer duration-300"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
});

MessageItem.displayName = "MessageItem";

const MessageList = memo(({ messages, user, formatTime, systemImage }) => {
  return (
    <>
      {messages.map((msg) => (
        <MessageItem
          key={msg.id}
          msg={msg}
          user={user}
          formatTime={formatTime}
          systemImage={systemImage}
        />
      ))}
    </>
  );
});

MessageList.displayName = "MessageList";

export default MessageList;
